import { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import logger from '../utils/logger';


/**
 * SessionManager component
 * 
 * This component handles token validation and session cleanup on app initialization.
 * It ensures that:
 * 1. Tokens are validated when the app starts
 * 2. Invalid tokens are cleared
 * 3. Sessions are properly managed across browser restarts
 */
const SessionManager = () => {
  const { validateToken, logout, isAuthenticated, setIsAuthenticated } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [isLoginPage, setIsLoginPage] = useState(false);

  // Fallback validation function in case validateToken is not available in UserContext
  const fallbackValidateToken = async (token) => {
    try {
      // Try multiple endpoints to validate the token
      let isValid = false;
      
      // Try the user info endpoint first
      try {
        await axios.get(`${API_URL}/users/info`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 3000 // Shorter timeout
        });
        logger.debug('Token validated with /users/info endpoint');
        isValid = true;
      } catch (primaryError) {
        if (primaryError.code === 'ECONNABORTED') {
          logger.debug('Validation timed out, assuming token is valid');
          return true; // Assume token is valid on timeout
        }
        
        logger.debug('Primary validation endpoint failed, trying fallback');
        
        // If that fails, try the user-specific endpoint
        try {
          const userId = localStorage.getItem('userId');
          if (!userId) throw new Error('No userId available for validation');
          
          await axios.get(`${API_URL}/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 3000 // Shorter timeout
          });
          logger.debug('Token validated with /users/{userId} endpoint');
          isValid = true;
        } catch (secondaryError) {
          if (secondaryError.code === 'ECONNABORTED') {
            logger.debug('Validation timed out, assuming token is valid');
            return true; // Assume token is valid on timeout
          }
          
          // If that also fails, try the MVP endpoint
          try {
            await axios.get(`${API_URL}/users/mvp`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 3000 // Shorter timeout
            });
            logger.debug('Token validated with /users/mvp endpoint');
            isValid = true;
          } catch (finalError) {
            if (finalError.code === 'ECONNABORTED') {
              logger.debug('Validation timed out, assuming token is valid');
              return true; // Assume token is valid on timeout
            }
            
            logger.error('All token validation attempts failed');
            // All attempts failed, token is invalid
          }
        }
      }
      
      if (!isValid) {
        // Only clear token if we're certain it's invalid (not on timeout)
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
      }
      
      return isValid;
    } catch (error) {
      // For general errors, if it's a timeout, assume token is valid
      if (error.code === 'ECONNABORTED') {
        logger.debug('Validation timed out, assuming token is valid');
        return true; // Assume token is valid on timeout
      }
      
      logger.error('Token validation failed in fallback:', error);
      // Clear token if invalid
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      return false; // Token is invalid
    }
  };

  // Function to check if token exists and validate it
  const checkSession = async () => {
    logger.debug('📋 SessionManager: Starting session check');
    
    // Check if we're on a login or registration page
    const isPublicRoute = [
      '/', 
      '/home', 
      '/login', 
      '/register', 
      '/new-profile', 
      '/forgot-password', 
      '/reset-password', 
      '/terms', 
      '/privacy'
    ].some(route => location.pathname === route || location.pathname.startsWith(route));
    
    setIsLoginPage(isPublicRoute);
    
    // If we're on a public route, we don't need to validate the token
    if (isPublicRoute) {
      setValidationAttempted(true);
      logger.debug('📋 On public route, skipping validation');
      return;
    }
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    logger.debug('📋 Token exists:', !!token);
    logger.debug('📋 UserId exists:', !!userId);
    
    // Check if token is in valid JWT format
    const isValidJwtFormat = token && token.split('.').length === 3;
    
    if (!token || !isValidJwtFormat) {
      logger.debug('📋 No valid token found, redirecting to login');
      navigate('/login');
      setValidationAttempted(true);
      logger.debug('📋 Session check completed, validationAttempted set to true');
      return;
    }
    
    // Check if token is expired by decoding it
    if (token && isValidJwtFormat) {
      try {
        // Get the payload part of the JWT
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        logger.debug('📋 Token expiration time:', new Date(payload.exp * 1000).toLocaleString());
        logger.debug('📋 Current time:', new Date(currentTime * 1000).toLocaleString());
        
        // Check if token is expired
        if (payload.exp && payload.exp < currentTime) {
          logger.debug('📋 Token is expired, clearing it');
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          navigate('/login');
          setValidationAttempted(true);
          logger.debug('📋 Session check completed, validationAttempted set to true');
          return;
        }
      } catch (error) {
        logger.error('📋 Error decoding token:', error);
        // If we can't decode the token, it's probably invalid
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        navigate('/login');
        setValidationAttempted(true);
        logger.debug('📋 Session check completed, validationAttempted set to true');
        return;
      }
    }
    
    if (token) {
      // If token exists, validate it
      logger.debug('📋 Session token found, validating...');
      logger.debug('📋 Token (first 10 chars):', token.substring(0, 10) + '...');
      
      try {
        // Check if validateToken function is available
        if (typeof validateToken === 'function') {
          logger.debug('📋 Using UserContext validateToken function');
          const validationResult = await validateToken(token);
          logger.debug('📋 UserContext validateToken completed, result:', validationResult);
          
          // CRITICAL: Get the current authentication state after validation
          // This ensures we're using the most up-to-date state
          const currentIsAuthenticated = isAuthenticated;
          logger.debug('📋 Current authentication state after validation:', currentIsAuthenticated);
          
          // Double-check that we're still authenticated after validation
          if (!currentIsAuthenticated) {
            // Check localStorage one more time as a fallback
            const hasToken = !!localStorage.getItem('token');
            const hasUserId = !!localStorage.getItem('userId');
            
            if (hasToken && hasUserId) {
              logger.debug('📋 Not authenticated in state but tokens exist in localStorage, staying on current page');
              // Don't redirect, let the PrivateRoute handle this
            } else {
              logger.debug('📋 Not authenticated after validation, redirecting to login');
              navigate('/login');
            }
          } else {
            logger.debug('📋 Authentication confirmed, staying on current page');
          }
        } else {
          // Use fallback validation if validateToken is not available
          logger.debug('📋 Using fallback token validation');
          const isValid = await fallbackValidateToken(token);
          logger.debug('📋 Fallback validation result:', isValid);
          if (!isValid) {
            logger.debug('📋 Token invalid, redirecting to login');
            navigate('/login');
          } else {
            // Set authentication state based on fallback validation
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        logger.error('📋 Session validation error:', error);
        logger.debug('📋 Error type:', error.name);
        logger.debug('📋 Error message:', error.message);
        logger.debug('📋 Error code:', error.code);
        logger.debug('📋 Response status:', error.response?.status);
        
        // Only redirect on auth errors, not on timeouts
        if (error.code !== 'ECONNABORTED') {
          logger.debug('📋 Error not a timeout, redirecting to login');
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          navigate('/login');
        } else {
          logger.debug('📋 Connection timeout, not redirecting');
          // Even on timeout, if we have a token, consider the user authenticated
          if (token && userId) {
            setIsAuthenticated(true);
          }
        }
      }
    } else {
      // If no token, redirect to login
      logger.debug('📋 No session token found, redirecting to login');
      navigate('/login');
    }
    
    setValidationAttempted(true);
    logger.debug('📋 Session check completed, validationAttempted set to true');
  };

  useEffect(() => {
    // Only run session check once and only if not on a login page
    if (!validationAttempted) {
      const isPublicRoute = [
        '/', 
        '/home', 
        '/login', 
        '/register', 
        '/new-profile', 
        '/forgot-password', 
        '/reset-password', 
        '/terms', 
        '/privacy'
      ].some(route => location.pathname === route || location.pathname.startsWith(route));
      
      if (!isPublicRoute) {
        checkSession();
      } else {
        setValidationAttempted(true);
      }
    }
    
    // Set up storage event listener to handle changes in other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        // Token was removed in another tab/window
        logger.debug('Token removed in another tab/window');
        
        const isPublicRoute = [
          '/', 
          '/home', 
          '/login', 
          '/register', 
          '/new-profile', 
          '/forgot-password', 
          '/reset-password', 
          '/terms', 
          '/privacy'
        ].some(route => location.pathname === route || location.pathname.startsWith(route));
        
        if (!isPublicRoute) {
          navigate('/login');
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [validateToken, navigate, location.pathname, validationAttempted, isLoginPage, isAuthenticated]);

  return null; // This component doesn't render anything
};

export default SessionManager;
